



import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";

function Sliders() {
    const [sliders, setSliders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const itemsPerPage = 10;

    /*
    |--------------------------------------------------------------------------
    | Image URL
    |--------------------------------------------------------------------------
    | Database stores only:
    | filename.jpg
    |
    | Actual file:
    | storage/app/public/sliders/filename.jpg
    |
    | Public URL:
    | https://apis.homoeoadda.in/storage/sliders/filename.jpg
    |--------------------------------------------------------------------------
    */
    const getImageUrl = (image) => {
    if (!image) return "";

    const baseUrl = API_BASE_URL.replace(/\/api\/?$/, "");

    // Old and new database formats
    const filename = String(image)
        .trim()
        .replace(/\\/g, "/")
        .split("/")
        .pop();

    if (!filename) return "";

    return `${baseUrl}/storage/sliders/${encodeURIComponent(filename)}`;
};

    /*
    |--------------------------------------------------------------------------
    | Fetch Sliders
    |--------------------------------------------------------------------------
    */
    const fetchSliders = async () => {
        try {
            setLoading(true);

            const response = await fetch(
                `${API_BASE_URL}/admin/sliders`,
                {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                    },
                    credentials: "include",
                }
            );

            const data = await response.json();

            if (response.ok && data.success) {
                setSliders(
                    Array.isArray(data.sliders) ? data.sliders : []
                );
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Unable to Load Sliders",
                    text: data.message || "Unable to fetch sliders.",
                });
            }
        } catch (error) {
            console.error("Fetch Sliders Error:", error);

            Swal.fire({
                icon: "error",
                title: "Server Error",
                text: "Unable to connect with server.",
            });
        } finally {
            setLoading(false);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Load Sliders On Component Mount
    |--------------------------------------------------------------------------
    */
    useEffect(() => {
        fetchSliders();
    }, []);

    /*
    |--------------------------------------------------------------------------
    | Delete Slider
    |--------------------------------------------------------------------------
    */
    const handleDelete = async (id) => {
        const result = await Swal.fire({
            icon: "warning",
            title: "Are you sure?",
            text: "This slider will be deleted permanently.",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            const response = await fetch(
                `${API_BASE_URL}/admin/sliders/${id}`,
                {
                    method: "DELETE",
                    headers: {
                        Accept: "application/json",
                    },
                    credentials: "include",
                }
            );

            const data = await response.json();

            if (response.ok && data.success) {
                setSliders((previousSliders) =>
                    previousSliders.filter(
                        (item) => String(item.id) !== String(id)
                    )
                );

                await Swal.fire({
                    icon: "success",
                    title: "Deleted!",
                    text: data.message || "Slider deleted successfully.",
                    timer: 1500,
                    showConfirmButton: false,
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Delete Failed",
                    text: data.message || "Unable to delete slider.",
                });
            }
        } catch (error) {
            console.error("Delete Slider Error:", error);

            Swal.fire({
                icon: "error",
                title: "Server Error",
                text: "Unable to connect with server.",
            });
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Search Sliders
    |--------------------------------------------------------------------------
    */
    const searchText = search.toLowerCase().trim();

    const filteredSliders = sliders.filter((item) => {
        return (
            String(item.id ?? "").toLowerCase().includes(searchText) ||
            String(item.image ?? "").toLowerCase().includes(searchText) ||
            String(item.badge ?? "").toLowerCase().includes(searchText) ||
            String(item.title ?? "").toLowerCase().includes(searchText) ||
            String(item.description ?? "").toLowerCase().includes(searchText) ||
            String(item.order ?? "").toLowerCase().includes(searchText)
        );
    });

    /*
    |--------------------------------------------------------------------------
    | Pagination
    |--------------------------------------------------------------------------
    */
    const totalPages = Math.ceil(
        filteredSliders.length / itemsPerPage
    );

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    const currentSliders = filteredSliders.slice(
        startIndex,
        endIndex
    );

    /*
    |--------------------------------------------------------------------------
    | Reset Page When Search Changes
    |--------------------------------------------------------------------------
    */
    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    /*
    |--------------------------------------------------------------------------
    | Correct Current Page After Delete / Search
    |--------------------------------------------------------------------------
    */
    useEffect(() => {
        if (totalPages === 0 && currentPage !== 1) {
            setCurrentPage(1);
        } else if (totalPages > 0 && currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    /*
    |--------------------------------------------------------------------------
    | Loading Screen
    |--------------------------------------------------------------------------
    */
    if (loading) {
        return (
            <div className="container-fluid py-5">
                <div className="text-center">
                    <div
                        className="spinner-border text-primary"
                        role="status"
                        aria-label="Loading"
                    />

                    <p className="mt-2 mb-0">
                        Loading sliders...
                    </p>
                </div>
            </div>
        );
    }

    /*
    |--------------------------------------------------------------------------
    | Component UI
    |--------------------------------------------------------------------------
    */
    return (
        <div
            className="container-fluid py-4"
            style={{
                maxHeight: "500px",
                overflowX: "auto",
                overflowY: "auto"
            }}
        >
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                <h3 className="mb-0">Sliders</h3>

                <Link
                    to="/add-slider"
                    className="btn btn-primary"
                >
                    + Add Slider
                </Link>
            </div>

            {/* Search */}
            <div className="card shadow-sm mb-3">
                <div className="card-body">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search by ID, badge, title, description, image or order..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* Sliders Table */}
            <div className="card shadow-sm">
                <div
                    className="card-body p-0"
                    style={{
                        maxHeight: "550px",
                        overflow: "auto",
                    }}
                >
                    <table className="table table-bordered table-hover table-striped align-middle mb-0">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: "60px" }}>#</th>
                                <th style={{ minWidth: "200px" }}>Image</th>
                                <th style={{ minWidth: "180px" }}>Badge</th>
                                <th style={{ minWidth: "250px" }}>Title</th>
                                <th style={{ minWidth: "350px" }}>Description</th>
                                <th style={{ width: "90px" }}>Order</th>
                                <th style={{ minWidth: "180px" }}>Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {currentSliders.length > 0 ? (
                                currentSliders.map((item, index) => (
                                    <tr key={item.id}>
                                        {/* Row Number */}
                                        <td>
                                            {startIndex + index + 1}
                                        </td>

                                        {/* Image */}
                                        <td>
                                            {item.image ? (
                                                <div>
                                                    <img
                                                        src={getImageUrl(item.image)}
                                                        alt={item.title || "Slider"}
                                                        loading="lazy"
                                                        style={{
                                                            width: "180px",
                                                            height: "75px",
                                                            objectFit: "cover",
                                                            borderRadius: "5px",
                                                            display: "block",
                                                            backgroundColor: "#f1f1f1",
                                                        }}
                                                        onError={(e) => {
                                                            console.error(
                                                                "Slider Image Failed:",
                                                                e.currentTarget.src
                                                            );
                                                            e.currentTarget.style.display = "none";
                                                        }}
                                                    />

                                                </div>
                                            ) : (
                                                <span className="text-muted">
                                                    No Image
                                                </span>
                                            )}
                                        </td>

                                        {/* Badge */}
                                        <td>
                                            {item.badge ? (
                                                <span className="badge bg-primary">
                                                    {item.badge}
                                                </span>
                                            ) : (
                                                <span className="text-muted">
                                                    —
                                                </span>
                                            )}
                                        </td>

                                        {/* Title */}
                                        <td>
                                            <div className="fw-semibold">
                                                {item.title || "—"}
                                            </div>
                                        </td>

                                        {/* Description */}
                                        <td>
                                            <div
                                                style={{
                                                    maxWidth: "400px",
                                                    whiteSpace: "normal",
                                                    overflowWrap: "anywhere",
                                                }}
                                            >
                                                {item.description || "—"}
                                            </div>
                                        </td>

                                        {/* Order */}
                                        <td>{item.order ?? 0}</td>

                                        {/* Actions */}
                                        <td>
                                            <div className="d-flex flex-wrap gap-2">
                                                <Link
                                                    to={`/edit-slider/${item.id}`}
                                                    className="btn btn-sm btn-warning"
                                                >
                                                    Edit
                                                </Link>

                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() =>
                                                        handleDelete(item.id)
                                                    }
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan="7"
                                        className="text-center py-4"
                                    >
                                        {search
                                            ? "No sliders match your search."
                                            : "No sliders found."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Table Footer / Pagination */}
                <div className="card-footer">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                            Showing{" "}
                            {filteredSliders.length > 0
                                ? startIndex + 1
                                : 0}
                            {" "}to{" "}
                            {Math.min(endIndex, filteredSliders.length)}
                            {" "}of{" "}
                            {filteredSliders.length}
                            {" "}sliders
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                disabled={currentPage <= 1}
                                onClick={() =>
                                    setCurrentPage((page) => page - 1)
                                }
                            >
                                Previous
                            </button>

                            <span className="text-nowrap">
                                Page {totalPages > 0 ? currentPage : 0} of{" "}
                                {totalPages}
                            </span>

                            <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                disabled={
                                    totalPages === 0 ||
                                    currentPage >= totalPages
                                }
                                onClick={() =>
                                    setCurrentPage((page) => page + 1)
                                }
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Sliders;
